import { Alert, Badge, Spinner } from 'react-bootstrap';
import type { SkinAnalysisPredictValidationResponse } from '@/models/SkinAnalysisPredictValidation';
import { PredictValidationResultPanel } from '@/pages/recommendation-lab/PredictValidationResultPanel';

interface PredictValidationProviderPanelProps {
  title: string;
  provider: string;
  result: SkinAnalysisPredictValidationResponse | null;
  isLoading: boolean;
  error: string | null;
  latencyMs: number | null;
}

/** Keeps each model's progress, failure, and customer preview independent during a comparison run. */
export function PredictValidationProviderPanel({
  title,
  provider,
  result,
  isLoading,
  error,
  latencyMs,
}: PredictValidationProviderPanelProps) {
  const status = isLoading ? 'Running' : error ? 'Failed' : result ? 'Complete' : 'Ready';
  const statusVariant = isLoading ? 'warning' : error ? 'danger' : result ? 'success' : 'secondary';

  return (
    <section className="validation-provider-panel" aria-label={`${title} result`} aria-busy={isLoading}>
      <header className="validation-provider-header">
        <div>
          <span className="validation-provider-eyebrow">{provider}</span>
          <h2>{title}</h2>
        </div>
        <div className="validation-provider-status">
          <Badge bg={statusVariant} text={statusVariant === 'warning' ? 'dark' : 'light'}>
            {status}
          </Badge>
          {latencyMs !== null && !isLoading && <span>{(latencyMs / 1000).toFixed(1)}s</span>}
        </div>
      </header>

      {isLoading && !result && (
        <div className="validation-provider-placeholder" role="status" aria-live="polite">
          <Spinner size="sm" />
          <span>Generating this model&apos;s customer result…</span>
        </div>
      )}
      {error && (
        <Alert variant="danger" className="validation-provider-error" role="alert">
          {title} failed: {error}
        </Alert>
      )}
      {!isLoading && !error && !result && (
        <div className="validation-provider-placeholder">Run the comparison to see this model&apos;s result.</div>
      )}
      {result && <PredictValidationResultPanel result={result} />}
    </section>
  );
}
